import React, { createContext, useContext, useState, useEffect } from 'react';

type Language = 'en' | 'pt-BR';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}

const translations = {
  'en': {
    // Navigation
    'nav.scanner': 'Scanner',
    'nav.generate': 'Generate',
    'nav.stats': 'Earnings',
    'nav.profile': 'Profile',
    'nav.scanner.desc': 'Validate coupons',
    'nav.generate.desc': 'Create QR codes',
    'nav.stats.desc': 'View commissions',
    'nav.profile.desc': 'My account',

    // Scanner Page
    'scanner.title': 'QR Scanner',
    'scanner.subtitle': 'Validate discount coupons',
    'scanner.camera.stopped': 'Camera Stopped',
    'scanner.camera.stopped.desc': 'Tap the button to start the scanner',
    'scanner.camera.instruction': '📱 Point at customer\'s QR code',
    'scanner.start': '▶️ Start Scanner',
    'scanner.stop': '⏹️ Stop Scanner',
    'scanner.status.active': 'ACTIVE',
    'scanner.status.stopped': 'STOPPED',
    'scanner.valid': 'Valid Coupon!',
    'scanner.invalid': 'Invalid Coupon',
    'scanner.connection.error': 'Connection Error',
    'scanner.connection.error.desc': 'Could not validate QR code. Check your connection.',
    'scanner.scan.error': 'Scanner Error',
    'scanner.next': '🔄 Scan Next',
    'scanner.retry': '🔄 Try Again',
    'scanner.close': '❌ Close',

    // Generate Page
    'generate.title': 'Generate QR Codes',
    'generate.subtitle': 'Create codes for your customers',
    'generate.phone.label': '📞 Customer Phone (Optional)',
    'generate.phone.placeholder': '+1 234 567-8900',
    'generate.no.coupons': 'No coupons available',
    'generate.no.coupons.desc': 'New coupons will appear here',
    'generate.loading': 'Loading coupons...',
    'generate.driver': '🚗 Driver:',
    'generate.usage': '📊 Usage:',
    'generate.used': 'used',
    'generate.generating': 'Generating...',
    'generate.exhausted': '❌ Coupon Exhausted',
    'generate.button': '📱 Generate QR Code',
    'generate.success': '✅ Code Generated',
    'generate.new': '🔄 Generate New',
    'generate.expires': '⏰ Expires in {minutes} minutes',
    'generate.show.desc': 'Show this code for customer to scan',
    'generate.error': '❌ Error generating QR: ',

    // Stats Page  
    'stats.title': 'My Earnings',
    'stats.subtitle': 'Commissions and statistics',
    'stats.period.week': 'Week',
    'stats.period.month': 'Month',
    'stats.period.all': 'Total',
    'stats.earnings.week': 'Week Earnings',
    'stats.earnings.month': 'Month Earnings',
    'stats.earnings.total': 'Total Earnings',
    'stats.commission.rate': 'Commission Rate',
    'stats.active.coupons': 'Active Coupons',
    'stats.total.redemptions': 'Total Redemptions',
    'stats.partners': 'Partners',
    'stats.this.week': 'This Week',
    'stats.recent.activity': 'Recent Activity',
    'stats.recent.desc': 'Latest coupon redemptions',
    'stats.paid': 'Paid',
    'stats.pix.title': '🏦 PIX Payments',
    'stats.pix.desc': 'Your commissions are automatically paid via PIX every Friday.',
    'stats.pix.setup': 'Setup PIX Key',
    'stats.loading': 'Loading earnings...',
    'stats.error': 'Error loading data',
    'stats.error.desc': 'Could not load your statistics',
    'stats.retry': 'Try Again',

    // Profile Page
    'profile.title': 'My Profile',
    'profile.subtitle': 'Manage personal information',
    'profile.edit': '✏️ Edit',
    'profile.verified': 'Verified',
    'profile.pending': 'Pending',
    'profile.rejected': 'Rejected',
    'profile.active.partners': 'Active Partners',
    'profile.total.earned': 'Total Earned',
    'profile.personal.info': 'Personal Information',
    'profile.full.name': 'Full Name',
    'profile.phone': 'Phone',
    'profile.email': 'Email',
    'profile.pix.key': 'PIX Key',
    'profile.pix.desc': 'To receive your commissions',
    'profile.uber.id': 'Uber Driver ID (Optional)',
    'profile.member.since': 'Member since',
    'profile.account.status': 'Account Status',
    'profile.status': 'Status',
    'profile.verification': 'Verification',
    'profile.active': 'Active',
    'profile.inactive': 'Inactive',
    'profile.save': '✅ Save Changes',
    'profile.cancel': '❌ Cancel',
    'profile.support': '📞 Support',
    'profile.terms': '📋 Terms of Use',
    'profile.logout': '🚪 Sign Out',
    'profile.loading': 'Loading profile...',
    'profile.error': 'Error loading profile',
    'profile.error.desc': 'Could not load your information',
    'profile.success': '✅ Profile updated successfully!',
    'profile.error.save': '❌ Error saving profile',

    // General
    'loading': 'Loading...',
    'error': 'Error',
    'success': 'Success',
    'cancel': 'Cancel',
    'save': 'Save',
    'close': 'Close',
    'retry': 'Try Again',
    'status.online': 'Online',
    'status.offline': 'Offline',

    // Language
    'language.english': 'English',
    'language.portuguese': 'Português (BR)',
    'language.switch': 'Language / Idioma',

    // Demo Flow
    'demo.welcome.title': 'Welcome to QRSP!',
    'demo.welcome.desc': 'Let\'s take a complete tour of the QR coupon platform',
    'demo.generate.title': 'Generate QR Codes',
    'demo.generate.desc': 'See how to generate QR codes for your customers',
    'demo.scanner.title': 'QR Scanner',
    'demo.scanner.desc': 'See how to validate customer coupons',
    'demo.stats.title': 'Your Earnings',
    'demo.stats.desc': 'Track your commissions and statistics',
  },
  'pt-BR': {
    // Navigation
    'nav.scanner': 'Scanner',
    'nav.generate': 'Gerar',
    'nav.stats': 'Ganhos',
    'nav.profile': 'Perfil',
    'nav.scanner.desc': 'Validar cupons',
    'nav.generate.desc': 'Criar QR codes',
    'nav.stats.desc': 'Ver comissões',
    'nav.profile.desc': 'Minha conta',

    // Scanner Page
    'scanner.title': 'Scanner QR',
    'scanner.subtitle': 'Validar cupons de desconto',
    'scanner.camera.stopped': 'Câmera Parada',
    'scanner.camera.stopped.desc': 'Toque no botão para iniciar o scanner',
    'scanner.camera.instruction': '📱 Aponte para o código QR do cliente',
    'scanner.start': '▶️ Iniciar Scanner',
    'scanner.stop': '⏹️ Parar Scanner',
    'scanner.status.active': 'ATIVO',
    'scanner.status.stopped': 'PARADO',
    'scanner.valid': 'Cupom Válido!',
    'scanner.invalid': 'Cupom Inválido',
    'scanner.connection.error': 'Erro de Conexão',
    'scanner.connection.error.desc': 'Não foi possível validar o código QR. Verifique sua conexão.',
    'scanner.scan.error': 'Erro de Scanner',
    'scanner.next': '🔄 Escanear Próximo',
    'scanner.retry': '🔄 Tentar Novamente',
    'scanner.close': '❌ Fechar',

    // Generate Page
    'generate.title': 'Gerar QR Codes',
    'generate.subtitle': 'Crie códigos para seus clientes',
    'generate.phone.label': '📞 Telefone do Cliente (Opcional)',
    'generate.phone.placeholder': '+55 11 99999-9999',
    'generate.no.coupons': 'Nenhum cupom disponível',
    'generate.no.coupons.desc': 'Novos cupons aparecerão aqui',
    'generate.loading': 'Carregando cupons...',
    'generate.driver': '🚗',
    'generate.usage': '📊',
    'generate.used': 'usados',
    'generate.generating': 'Gerando...',
    'generate.exhausted': '❌ Cupom Esgotado',
    'generate.button': '📱 Gerar QR Code',
    'generate.success': '✅ Código Gerado',
    'generate.new': '🔄 Gerar Novo',
    'generate.expires': '⏰ Expira em {minutes} minutos',
    'generate.show.desc': 'Motre este código para o cliente escanear',
    'generate.error': '❌ Erro ao gerar QR: ',

    // Stats Page
    'stats.title': 'Meus Ganhos',
    'stats.subtitle': 'Comissões e estatísticas',
    'stats.period.week': 'Semana',
    'stats.period.month': 'Mês',
    'stats.period.all': 'Total',
    'stats.earnings.week': 'Ganhos da Semana',
    'stats.earnings.month': 'Ganhos do Mês',
    'stats.earnings.total': 'Ganhos Total',
    'stats.commission.rate': 'Taxa de Comissão',
    'stats.active.coupons': 'Cupons Ativos',
    'stats.total.redemptions': 'Resgates Total',
    'stats.partners': 'Parceiros',
    'stats.this.week': 'Esta Semana',
    'stats.recent.activity': 'Atividade Recente',
    'stats.recent.desc': 'Últimos resgates de cupons',
    'stats.paid': 'Pago',
    'stats.pix.title': '🏦 Pagamentos via PIX',
    'stats.pix.desc': 'Suas comissões são pagas automaticamente via PIX toda sexta-feira.',
    'stats.pix.setup': 'Configurar Chave PIX',
    'stats.loading': 'Carregando ganhos...',
    'stats.error': 'Erro ao carregar dados',
    'stats.error.desc': 'Não foi possível carregar suas estatísticas',
    'stats.retry': 'Tentar Novamente',

    // Profile Page
    'profile.title': 'Meu Perfil',
    'profile.subtitle': 'Gerenciar informações pessoais',
    'profile.edit': '✏️ Editar',
    'profile.verified': 'Verificado',
    'profile.pending': 'Pendente',
    'profile.rejected': 'Rejeitado',
    'profile.active.partners': 'Parceiros Ativos',
    'profile.total.earned': 'Total Ganho',
    'profile.personal.info': 'Informações Pessoais',
    'profile.full.name': 'Nome Completo',
    'profile.phone': 'Telefone',
    'profile.email': 'Email',
    'profile.pix.key': 'Chave PIX',
    'profile.pix.desc': 'Para receber suas comissões',
    'profile.uber.id': 'ID Uber Driver (Opcional)',
    'profile.member.since': 'Membro desde',
    'profile.account.status': 'Status da Conta',
    'profile.status': 'Status',
    'profile.verification': 'Verificação',
    'profile.active': 'Ativo',
    'profile.inactive': 'Inativo',
    'profile.save': '✅ Salvar Alterações',
    'profile.cancel': '❌ Cancelar',
    'profile.support': '📞 Suporte',
    'profile.terms': '📋 Termos de Uso',
    'profile.logout': '🚪 Sair da Conta',
    'profile.loading': 'Carregando perfil...',
    'profile.error': 'Erro ao carregar perfil',
    'profile.error.desc': 'Não foi possível carregar suas informações',
    'profile.success': '✅ Perfil atualizado com sucesso!',
    'profile.error.save': '❌ Erro ao salvar perfil',

    // General
    'loading': 'Carregando...',
    'error': 'Erro',
    'success': 'Sucesso',
    'cancel': 'Cancelar',
    'save': 'Salvar',
    'close': 'Fechar',
    'retry': 'Tentar Novamente',
    'status.online': 'Online',
    'status.offline': 'Offline',

    // Language
    'language.english': 'English',
    'language.portuguese': 'Português (BR)',
    'language.switch': 'Language / Idioma',

    // Demo Flow
    'demo.welcome.title': 'Bem-vindo ao QRSP!',
    'demo.welcome.desc': 'Vamos fazer um tour completo pela plataforma de cupons QR',
    'demo.generate.title': 'Gerar QR Codes',
    'demo.generate.desc': 'Veja como gerar códigos QR para seus clientes',
    'demo.scanner.title': 'Scanner QR',
    'demo.scanner.desc': 'Veja como validar cupons dos clientes',
    'demo.stats.title': 'Seus Ganhos',
    'demo.stats.desc': 'Acompanhe suas comissões e estatísticas',
  }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: React.ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const stored = localStorage.getItem('qrsp-language');
    if (stored && (stored === 'en' || stored === 'pt-BR')) {
      return stored as Language;
    }
    
    // Auto-detect based on browser language
    const browserLang = navigator.language;
    if (browserLang.startsWith('pt')) {
      return 'pt-BR';
    }
    return 'en';
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('qrsp-language', lang);
  };

  const t = (key: string, params?: Record<string, string | number>): string => {
    let translation = (translations[language] as any)[key] || (translations['en'] as any)[key] || key;
    
    if (params) {
      Object.entries(params).forEach(([param, value]) => {
        translation = translation.replace(`{${param}}`, String(value));
      });
    }
    
    return translation;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};